import PropTypes from 'prop-types'
import React from 'react'
import addClass from 'dom-helpers/cjs/addClass'
import removeClass from 'dom-helpers/cjs/removeClass'
import width from 'dom-helpers/cjs/width'
import scrollbarSize from 'dom-helpers/cjs/scrollbarSize'
import { useNavigate } from 'react-router-dom';

import {add, eq, lt, gt, startOf, endOf, range} from 'react-big-calendar/lib/utils/dates'
import { navigate } from 'react-big-calendar/lib/utils/constants'
import { inRange } from 'react-big-calendar/lib/utils/eventLevels'
import { isSelected } from 'react-big-calendar/lib/utils/selection'
import moment from 'moment';

// Define these correctly BEFORE the class definition
function agendaNavigate(date, action, { length = 30 }) {
  switch (action) {
    case navigate.PREVIOUS:
      return add(date, -length, 'day')

    case navigate.NEXT:
      return add(date, length, 'day')

    default:
      return date
  }
}

function agendaRange(start, { length = 30 }) {
  // Properly format the date
  const normalizedStart = new Date(start);
  const end = add(normalizedStart, length, 'day');
  return { start: normalizedStart, end };
}

function agendaTitle(start, { length = 30, localizer }) {
  // Properly format the date
  const normalizedStart = new Date(start);
  const end = add(normalizedStart, length, 'day');
  return localizer.format({ start: normalizedStart, end }, 'agendaHeaderFormat');
}

class CustomAgendaView extends React.Component {
	static propTypes = {
		events: PropTypes.array,
		date: PropTypes.instanceOf(Date),
		length: PropTypes.number.isRequired,
		navigate: PropTypes.func,
		selected: PropTypes.object,
		accessors: PropTypes.object.isRequired,
		components: PropTypes.object.isRequired,
		getters: PropTypes.object.isRequired,
		localizer: PropTypes.object.isRequired,
	}

	static defaultProps = {
		length: 30
	}

	componentDidMount() {
		this._adjustHeader()
	}

	componentDidUpdate() {
		this._adjustHeader()
	}
	
	render() {
		let { length, date, events, accessors, localizer } = this.props
		let { messages } = localizer

		const d2 = moment(date);
		d2.set({
			'hour': 0,
			'minute': 0,
			'second': 0
		});
		date = d2.toDate();

		let end = add(date, length, 'day')

		let rng = range(date, end, 'day')

		events = events.filter(event => moment(date).isSame(moment(event.start), 'day'))

		events.sort((a, b) => +accessors.start(a) - +accessors.start(b))

		return (
			<div className="rbc-agenda-view" style={{zIndex:"50"}}>
				{events.length !== 0 ? (
					<React.Fragment>
						<table ref="header" className="rbc-agenda-table">
							<thead>
								<tr>
									<th className="rbc-header">Patient</th>
									<th className="rbc-header">ID Card</th>
									<th className="rbc-header" ref="dateCol">
										{messages.date}
									</th>
									<th className="rbc-header" ref="timeCol">
										{messages.time}
									</th>
									<th className="rbc-header">Mobile</th>
								</tr>
							</thead>
							<tbody ref="content">
								{rng.map((day, idx) => this.renderDay(day, events, idx))}
							</tbody>
						</table>
					</React.Fragment>
				) : (
						<span className="rbc-agenda-empty">{messages.noEventsInRange}</span>
					)}
			</div>
		)
	}

	renderDay = (day, events, dayKey) => {
		let {
			selected,
			getters,
			accessors,
			localizer,
			components: { event: Event, date: AgendaDate },
		} = this.props

		events = events.filter(e =>
			inRange(e, startOf(day, 'day'), endOf(day, 'day'), accessors)
		)

		const { showEventTypes } = this.props;

		return events.map((event, idx) => {
			if (showEventTypes && showEventTypes.indexOf(event.type) !== -1) {
				let title = accessors.title(event)
				let end = accessors.end(event)
				let start = accessors.start(event)

				const userProps = getters.eventProp(
					event,
					start,
					end,
					isSelected(event, selected)
				)

				let dateLabel = idx === 0 && localizer.format(day, 'agendaDateFormat')
				let first =
					idx === 0 ? (
						<td rowSpan={events.length} className="rbc-agenda-date-cell">
							{AgendaDate ? (
								<AgendaDate day={day} label={dateLabel} />
							) : (
									dateLabel
								)}
						</td>
					) : (
							false
						)

					// Enhanced patient information display
				let patientInfo = title;
				
				 // Get mobile number with country code
				let mobileNumber = '';
				let fullMobileNumber = '';
				if (event.mobile) {
					if (typeof event.mobile === 'object') {
						const number = event.mobile.mobileNumber || event.mobile.number || '';
						const countryCode = event.mobile.countryPrefixCode || '356'; // Default to Malta
						mobileNumber = number;
						fullMobileNumber = number ? `+${countryCode}${number}` : '';
					} else {
						mobileNumber = event.mobile;
						fullMobileNumber = event.mobile;
					}
				}
				
				// Only append patient details for filled appointments
				if (event.type === 'filled') {
					// Create full patient details for the tooltip
					let tooltipContent = "";
					if (event.idCard) tooltipContent += `ID: ${event.idCard}\n`;
					
					// Add phone numbers if available
					if (fullMobileNumber) tooltipContent += `Mobile: ${fullMobileNumber}\n`;
					
					if (event.landline) {
						const landlineNumber = typeof event.landline === 'object' ? 
							(event.landline.landlineNumber || event.landline.number) : 
							event.landline;
						
						if (landlineNumber) tooltipContent += `Tel: ${landlineNumber}\n`;
					}
					
					// Add pharmacy remarks if available
					if (event.pharmacyRemarks) {
						tooltipContent += `Notes: ${event.pharmacyRemarks}\n`;
					}
				}

				const handleRowClick = (creationTimestamp, item) => {
					if (item && item.type && item.type === "filled") {
						this.props.navigate(`/appointments/${creationTimestamp}`, {
							state: {
								item,
								refreshRequired: true
							}
						});
					}
				}
				return (

					<tr
						key={dayKey + '_' + idx}
						className={userProps.className}
						style={userProps.style}
						onClick={() => handleRowClick(event.creationTimestamp, event.appointment)}
						name="agendaRow"
						title={event.type === 'filled' ? `${event.idCard ? 'ID: ' + event.idCard : ''}${fullMobileNumber ? '\nMobile: ' + fullMobileNumber : ''}` : ''}
					>
						<td className="rbc-agenda-event-cell">
							{Event ? <Event event={event} title={patientInfo} /> : patientInfo}
						</td>
						<td style={{fontWeight: 'bold'}}>
							{event.type === 'filled' && event.idCard ? event.idCard : ''}
						</td>
						{first}
						<td className="rbc-agenda-time-cell">
							{this.timeRangeLabel(day, event)}
						</td>
						<td>
							{event.type === 'filled' && fullMobileNumber ? fullMobileNumber : ''}
						</td>
					</tr>
				)
			} else return null;
		}, [])
	}

	timeRangeLabel = (day, event) => {
		let { accessors, localizer, components } = this.props

		let labelClass = '',
			TimeComponent = components.time,
			label = localizer.messages.allDay

		let end = accessors.end(event)
		let start = accessors.start(event)

		if (!accessors.allDay(event)) {
			if (eq(start, end, 'day')) {
				label = localizer.format({ start, end }, 'agendaTimeRangeFormat')
			} else if (eq(day, start, 'day')) {
				label = localizer.format(start, 'agendaTimeFormat')
			} else if (eq(day, end, 'day')) {
				label = localizer.format(end, 'agendaTimeFormat')
			}
		}

		if (gt(day, start, 'day')) labelClass = 'rbc-continues-prior'
		if (lt(day, end, 'day')) labelClass += ' rbc-continues-after'

		return (
			<span className={labelClass.trim()}>
				{TimeComponent ? (
					<TimeComponent event={event} day={day} label={label} />
				) : (
						label
					)}
			</span>
		)
	}

	_adjustHeader = () => {
		if (!this.refs?.content) return

		let header = this.refs.header
		let firstRow = this.refs.content.firstChild

		if (!firstRow) return

		let isOverflowing =
			this.refs.content.scrollHeight > this.refs.content.clientHeight
		let widths = this._widths || []

		this._widths = [
			width(firstRow.children[0]),
			width(firstRow.children[1]),
		]

		if (widths[0] !== this._widths[0] || widths[1] !== this._widths[1]) {
			this.refs.dateCol.style.width = this._widths[0] + 'px'
			this.refs.timeCol.style.width = this._widths[1] + 'px'
		}

		if (isOverflowing) {
			addClass(header, 'rbc-header-overflowing')
			header.style.marginRight = scrollbarSize() + 'px'
		} else {
			removeClass(header, 'rbc-header-overflowing')
		}
	}
}

// Replace the static properties with the proper function references
CustomAgendaView.navigate = agendaNavigate;
CustomAgendaView.range = agendaRange;
CustomAgendaView.title = agendaTitle;

// Custom wrapper component to provide navigate function from useNavigate hook
const CustomAgendaViewWrapper = (props) => {
  const navigate = useNavigate();
  return <CustomAgendaView {...props} navigate={navigate} />;
};

export default CustomAgendaViewWrapper;